import { FC, useState } from 'react'
import {
  List,
  ListProps,
  useListContext,
  RecordContextProvider,
} from 'react-admin'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Chip,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import WaterDropIcon from '@mui/icons-material/WaterDrop'
import { ConversationOut } from '../../types/apiRequests'
import ConversationDetails from './Conversation.details'

const TEN_DAYS_MS = 10 * 24 * 60 * 60 * 1000
const DAY_MS = 24 * 60 * 60 * 1000

const StatusIcon: FC<{ created?: string | Date }> = ({ created }) => {
  if (!created) {
    return <WaterDropIcon sx={{ color: '#BDBDBD' }} />
  }

  const createdAt = new Date(created)
  const now = new Date()
  const age = now.getTime() - createdAt.getTime()

  if (!Number.isFinite(age)) {
    return <WaterDropIcon sx={{ color: '#BDBDBD' }} />
  }

  if (age < DAY_MS) {
    return <NotificationsActiveIcon sx={{ color: '#CB8418' }} />
  }

  if (age > TEN_DAYS_MS) {
    return <LocalFireDepartmentIcon sx={{ color: 'red' }} />
  }

  return <WaterDropIcon sx={{ color: '#747476' }} />
}

const ConversationGrid: FC = () => {
  const { data, isPending, error } = useListContext<ConversationOut>()
  const [selectedConversation, setSelectedConversation] =
    useState<ConversationOut | null>(null)

  if (isPending || !data) return null
  if (error) return <div>Error</div>

  const handleCardClick = (record: ConversationOut) => {
    setSelectedConversation(record)
  }

  const handleClose = () => {
    setSelectedConversation(null)
  }

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: '16px',
        }}
      >
        {data.map((record) => (
          <RecordContextProvider key={record.id} value={record}>
            <Card
              onClick={() => handleCardClick(record)}
              sx={{
                cursor: 'pointer',
                minHeight: '150px',
                backgroundColor: '#D5E0EB',
                borderRadius: '12px',
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    component="div"
                    variant="body2"
                    sx={{ fontWeight: 'bold', whiteSpace: 'pre-wrap', flexGrow: 1 }}
                  >
                    {record.description}
                  </Typography>

                  <StatusIcon created={record.created} />
                </Box>

                <Typography variant="body2" sx={{ mt: 1 }}>
                  xxllnc (Bouwmeester)
                </Typography>

                <Chip
                  label="Zaken"
                  size="small"
                  sx={{ p: 2, mt: 3, backgroundColor: '#FAEBD4' }}
                />
              </CardContent>
            </Card>
          </RecordContextProvider>
        ))}
      </Box>

      {selectedConversation && (
        <Dialog
          open={!!selectedConversation}
          onClose={handleClose}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <RecordContextProvider value={selectedConversation}>
              <ConversationDetails />
            </RecordContextProvider>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}

const ConversationList: FC<ListProps> = (props) => (
  <List
    {...props}
    exporter={false}
    pagination={false}
    component="div"
    sort={{ field: 'created', order: 'DESC' }}
    actions={false}
  >
    <ConversationGrid />
  </List>
)

export default ConversationList
